/*
 *  Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
 *
 *  Licensed under the Apache License, Version 2.0 (the "License").
 *  You may not use this file except in compliance with the License.
 *  A copy of the License is located at
 *
 *  http://aws.amazon.com/apache2.0
 *
 *  or in the "license" file accompanying this file. This file is distributed
 *  on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either
 *  express or implied. See the License for the specific language governing
 *  permissions and limitations under the License.
 */

/* eslint-disable import/prefer-default-export */
import _ from 'lodash';
import { types, applySnapshot } from 'mobx-state-tree';

import {
  describeMirroredSchedule,
  describeMirroredIdleStop,
  describeIdleStopEvent,
} from '../../helpers/schedule-display';

// 'COMPLETED', 'PENDING', 'TAINTED', 'FAILED', 'TERMINATING', 'TERMINATED', 'TERMINATING_FAILED', 'INSUFFICIENT_FUNDS', 'EXPIRED', 'UNKNOWN'
// Note: 'UNKNOWN' is not something that is returned from the server, it is here to catch any other status
// that we don't know about.
const states = [
  {
    key: 'COMPLETED',
    display: 'RUNNING',
    color: 'green',
    hexColor: '#16a34a',
    hexBg: '#f0fdf4',
    tip: 'The workspace is ready to be used.',
    spinner: false,
    canTerminate: true,
    canConnect: true,
    canStop: true,
    canStart: false,
    canChangeInstanceType: false,
    canModifyStorageSize: false,
    canSetSchedule: true,
  },
  {
    key: 'COMPLETED_EXPIRED',
    display: 'RUNNING',
    color: 'green',
    hexColor: '#16a34a',
    hexBg: '#f0fdf4',
    tip: 'The workspace is ready to be used. Note: This workspace has expired and will automatically stop soon.',
    spinner: false,
    canTerminate: true,
    canConnect: true,
    canStop: true,
    canStart: false,
    canChangeInstanceType: false,
    canModifyStorageSize: false,
    canSetSchedule: true,
  },
  {
    key: 'PENDING',
    display: 'PROVISIONING..',
    color: 'yellow',
    hexColor: '#2563eb',
    hexBg: '#eff6ff',
    tip: 'The workspace is being built, this will take between 5 and 15 minutes.',
    spinner: true,
    canTerminate: false,
    canConnect: false,
    canStop: false,
    canStart: false,
    canChangeInstanceType: false,
    canModifyStorageSize: false,
    canSetSchedule: false,
  },
  {
    key: 'STOPPED',
    display: 'STOPPED',
    color: 'orange',
    hexColor: '#ea580c',
    hexBg: '#fff7ed',
    tip: 'The workspace is stopped.',
    spinner: false,
    canTerminate: true,
    canConnect: false,
    canStop: false,
    canStart: true,
    canChangeInstanceType: true,
    canModifyStorageSize: true,
    canSetSchedule: true,
  },
  {
    key: 'STOPPING',
    display: 'STOPPING..',
    color: 'orange',
    hexColor: '#ea580c',
    hexBg: '#fff7ed',
    tip: 'The workspace is stopping.',
    spinner: true,
    canTerminate: false,
    canConnect: false,
    canStop: false,
    canStart: false,
    canChangeInstanceType: false,
    canModifyStorageSize: false,
    canSetSchedule: false,
  },
  {
    key: 'STOPPING_FAILED',
    display: 'CONTACT RACE: STOP FAILED',
    color: 'red',
    hexColor: '#dc2626',
    hexBg: '#fef2f2',
    tip: 'The workspace failed to stop. The system will attempt to reset this status automatically.',
    spinner: true,
    canTerminate: false,
    canConnect: false,
    canStop: false,
    canStart: false,
    canChangeInstanceType: false,
    canModifyStorageSize: false,
    canSetSchedule: false,
  },
  {
    key: 'STARTING',
    display: 'STARTING..',
    color: 'orange',
    hexColor: '#ea580c',
    hexBg: '#fff7ed',
    tip: 'The workspace is starting.',
    spinner: true,
    canTerminate: false,
    canConnect: false,
    canStop: false,
    canStart: false,
    canChangeInstanceType: false,
    canModifyStorageSize: false,
    canSetSchedule: false,
  },
  {
    key: 'STARTING_FAILED',
    display: 'CONTACT RACE: START FAILED',
    color: 'red',
    hexColor: '#dc2626',
    hexBg: '#fef2f2',
    tip: 'The workspace failed to start. The system will attempt to reset this status automatically.',
    spinner: true,
    canTerminate: false,
    canConnect: false,
    canStop: false,
    canStart: false,
    canChangeInstanceType: false,
    canModifyStorageSize: false,
    canSetSchedule: false,
  },
  {
    key: 'MODIFYING',
    display: 'MODIFYING..',
    color: 'orange',
    hexColor: '#ea580c',
    hexBg: '#fff7ed',
    tip: 'The workspace is being modified.',
    spinner: true,
    canTerminate: false,
    canConnect: false,
    canStop: false,
    canStart: false,
    canChangeInstanceType: false,
    canModifyStorageSize: false,
    canSetSchedule: false,
  },
  {
    key: 'MODIFYING_FAILED',
    display: 'MODIFY FAILED',
    color: 'red',
    hexColor: '#dc2626',
    hexBg: '#fef2f2',
    tip: 'The workspace modification failed. You can try again or start the workspace.',
    spinner: false,
    canTerminate: true,
    canConnect: false,
    canStop: false,
    canStart: true,
    canChangeInstanceType: true,
    canModifyStorageSize: true,
    canSetSchedule: false,
  },
  {
    key: 'RESTARTING',
    display: 'RESTARTING..',
    color: 'orange',
    hexColor: '#ea580c',
    hexBg: '#fff7ed',
    tip: 'The workspace is restarting.',
    spinner: true,
    canTerminate: false,
    canConnect: false,
    canStop: false,
    canStart: false,
    canChangeInstanceType: false,
    canModifyStorageSize: false,
    canSetSchedule: false,
  },
  {
    key: 'TAINTED',
    display: 'CONTACT RACE: TAINTED',
    color: 'orange',
    hexColor: '#ea580c',
    hexBg: '#fff7ed',
    tip: 'The workspace is ready but the latest configuration updates might not have been successful.',
    spinner: false,
    canTerminate: true,
    canConnect: true,
    canStop: false,
    canStart: false,
    canChangeInstanceType: false,
    canModifyStorageSize: false,
    canSetSchedule: false,
  },
  {
    key: 'FAILED',
    display: 'CONTACT RACE: FAILED',
    color: 'red',
    hexColor: '#dc2626',
    hexBg: '#fef2f2',
    tip: 'Something went wrong. Feel free to try again or contact the RACE team for support.',
    spinner: false,
    canTerminate: true,
    canConnect: false,
    canStop: false,
    canStart: false,
    canChangeInstanceType: false,
    canModifyStorageSize: false,
    canSetSchedule: false,
  },
  {
    key: 'TERMINATING',
    display: 'DELETING..',
    color: 'red',
    hexColor: '#dc2626',
    hexBg: '#fef2f2',
    tip: 'The workspace is being terminated. This could take sometime.',
    spinner: true,
    canTerminate: false,
    canConnect: false,
    canStop: false,
    canStart: false,
    canChangeInstanceType: false,
    canModifyStorageSize: false,
    canSetSchedule: false,
  },
  {
    key: 'TERMINATED',
    display: 'DELETED',
    color: 'grey',
    hexColor: '#6b7280',
    hexBg: '#f3f4f6',
    tip: 'The workspace was deleted successfully and no longer available.',
    spinner: false,
    canTerminate: false,
    canConnect: false,
    canStop: false,
    canStart: false,
    canChangeInstanceType: false,
    canModifyStorageSize: false,
    canSetSchedule: false,
  },
  {
    key: 'TERMINATING_FAILED',
    display: 'CONTACT RACE: DELETE FAILED',
    color: 'red',
    hexColor: '#dc2626',
    hexBg: '#fef2f2',
    tip:
      'The workspace was not terminated correctly, it is possible that some compute and storage resources are still in place.',
    spinner: false,
    canTerminate: true,
    canConnect: true,
    canStop: false,
    canStart: false,
    canChangeInstanceType: false,
    canModifyStorageSize: false,
    canSetSchedule: false,
  },
  {
    key: 'INSUFFICIENT_FUNDS',
    display: 'CONTACT RACE: NOT ENOUGH CREDIT',
    color: 'purple',
    hexColor: '#9333ea',
    hexBg: '#faf5ff',
    tip: 'The workspace was not started due to insufficient credit. Contact RACE for more information.',
    spinner: false,
    canTerminate: true,
    canConnect: false,
    canStop: false,
    canStart: true,
    canChangeInstanceType: false,
    canModifyStorageSize: false,
    canSetSchedule: false,
  },
  {
    key: 'EXPIRED',
    display: 'EXPIRED',
    color: 'black',
    hexColor: '#1f2937',
    hexBg: '#f3f4f6',
    tip: 'This workspace has expired. Terminate the workspace, or contact RACE to recover your data.',
    spinner: false,
    canTerminate: true,
    canConnect: false,
    canStop: false,
    canStart: false,
    canChangeInstanceType: false,
    canModifyStorageSize: false,
    canSetSchedule: false,
  },
  {
    key: 'UNKNOWN',
    display: 'CONTACT RACE: INTERNAL ERROR',
    color: 'blue',
    hexColor: '#2563eb',
    hexBg: '#eff6ff',
    tip: 'Something went wrong. This requires further investigation by the RACE team.',
    spinner: false,
    canTerminate: true,
    canConnect: false,
    canStop: false,
    canStart: false,
    canChangeInstanceType: false,
    canModifyStorageSize: false,
    canSetSchedule: false,
  },
];

// ==================================================================
// ScEnvironment
// Added root volume for custom storage
// ==================================================================
// Grace period (in ms) after a status change during which heartbeat updates are skipped
// This prevents the UI from reverting to the old status before the backend processes the change
const STATUS_CHANGE_GRACE_PERIOD = 10000; // 10 seconds

const ScEnvironment = types
  .model('ScEnvironment', {
    id: types.identifier,
    rev: types.maybe(types.number),
    status: '',
    description: '',
    envTypeConfigId: '',
    enableHyperthreading: types.optional(types.boolean, false),
    name: '',
    rootVolume: '',
    projectId: '',
    envTypeId: '',
    createdAt: '',
    createdBy: '',
    updatedAt: '',
    updatedBy: '',
    expiresAt: '',
    optionalSecurityGroupId: '',
    error: types.maybeNull(types.string),
    connections: types.frozen([]),
    hasConnections: false,
    studyIds: types.frozen([]),
    cidr: types.frozen([]),
    outputs: types.frozen([]),
    isAppStreamConfigured: types.optional(types.boolean, false),
    terminationLocked: types.optional(types.boolean, false),
    diskUtilizationGB: types.maybeNull(types.number),
    cpuUtilization: types.maybeNull(types.number),
    cpuUtilizationUpdatedAt: types.maybeNull(types.string),
    targetRegion: types.optional(types.string, ''),
    capacityError: types.optional(types.boolean, false),
    // Tag_Mirror: a copy of the scheduling tags on the workspace's EC2 instance, maintained by the
    // status poll and sync and by the scheduling service, so that lists and cards render without an
    // AWS call per workspace. The instance tags remain the single source of truth (Requirement 11.8);
    // these fields are a display and recovery convenience only. All are nullable because every tag is
    // optional, and the four reserved ones are written only by the external scheduler.
    schedule: types.maybeNull(types.string),
    idleStopMinutes: types.maybeNull(types.number),
    scheduleLastStart: types.maybeNull(types.string),
    scheduleLastStop: types.maybeNull(types.string),
    idleStopWarnedAt: types.maybeNull(types.string),
    // When the scheduler last stopped this workspace for being idle. The scheduler never clears it, so
    // it outlives the stop itself: a workspace since restarted still carries the record of having been
    // idle-stopped, which is the fact a user looking at a stopped workspace wants.
    idleStopStoppedAt: types.maybeNull(types.string),
    // Removing a tag is the only way the tag contract offers to pause a schedule, and it delegates
    // remembering the prior value to the client, so that it can be offered back.
    previousSchedule: types.maybeNull(types.string),
    previousIdleStopMinutes: types.maybeNull(types.number),
    // Timestamp when status was last changed locally (for grace period logic)
    statusChangedAt: types.optional(types.number, 0),
  })
  .actions(self => ({
    setScEnvironment(rawEnvironment) {
      // Note: if you have partial data vs full data, you need to replace the applySnapshot() with
      // the appropriate logic
      const raw = { ...rawEnvironment, connections: self.connections || [] };
      // Coerce rootVolume to string (backend returns number, MST model expects string)
      if (raw.rootVolume !== undefined && raw.rootVolume !== null) {
        raw.rootVolume = String(raw.rootVolume);
      } else if (raw.rootVolume === undefined && self.rootVolume) {
        raw.rootVolume = self.rootVolume;
      }
      if (_.isString(raw.cidr) && _.isArray(self.cidr)) raw.cidr = self.cidr;

      // Skip status updates during grace period after a local status change
      // This prevents heartbeat from reverting status before backend processes the change
      const isInGracePeriod =
        self.statusChangedAt > 0 && Date.now() - self.statusChangedAt < STATUS_CHANGE_GRACE_PERIOD;

      if (isInGracePeriod && raw.status !== self.status) {
        // Within grace period and API returned different status - keep current status, update everything else
        const preservedStatus = self.status;
        const preservedStatusChangedAt = self.statusChangedAt;
        applySnapshot(self, raw);
        self.status = preservedStatus;
        self.statusChangedAt = preservedStatusChangedAt;
        return;
      }

      // Normal update - grace period expired or status matches
      // Clear the grace period timestamp since we're accepting the server status
      applySnapshot(self, raw);
      self.statusChangedAt = 0;
    },
    setStatus(status) {
      self.status = status;
      // Record when status was changed locally to enable grace period
      self.statusChangedAt = Date.now();
    },
    setConnections(connections) {
      self.connections = connections;
    },

    // Applies the scheduling state returned by the set-schedule and set-idle-stop endpoints.
    //
    // Deliberately narrow. `setScEnvironment` applies a whole snapshot, which would discard fields the
    // scheduling response does not carry, and it participates in the status grace period; a scheduling
    // change alters no lifecycle status at all (Requirements 5.11 and 7.9), so it has no business
    // going near either. Only the mirror fields are assigned here.
    //
    // Takes the SchedulingInfo the endpoints return, reading the mirror fields out of its `schedule`
    // and `idleStop` blocks. A block missing from the payload means that half of the state was not
    // part of this write and is left alone; within a block a missing field means the tag is absent,
    // which is null.
    setSchedulingState(schedulingInfo = {}) {
      const { schedule, idleStop } = schedulingInfo;

      if (_.isPlainObject(schedule)) {
        self.schedule = _.isNil(schedule.value) || schedule.value === '' ? null : schedule.value;
        self.previousSchedule =
          _.isNil(schedule.previousValue) || schedule.previousValue === '' ? null : schedule.previousValue;
        self.scheduleLastStart = _.isNil(schedule.lastStartAt) ? null : schedule.lastStartAt;
        self.scheduleLastStop = _.isNil(schedule.lastStopAt) ? null : schedule.lastStopAt;
      }

      if (_.isPlainObject(idleStop)) {
        self.idleStopMinutes = _.isNil(idleStop.minutes) ? null : Number(idleStop.minutes);
        self.previousIdleStopMinutes = _.isNil(idleStop.previousMinutes) ? null : Number(idleStop.previousMinutes);
        self.idleStopWarnedAt = _.isNil(idleStop.warnedAt) ? null : idleStop.warnedAt;
        self.idleStopStoppedAt = _.isNil(idleStop.stoppedAt) ? null : idleStop.stoppedAt;
      }
    },
  }))

  // eslint-disable-next-line no-unused-vars
  .views(self => ({
    // State is a generalization of the status name. With the state object, we can provide more
    // information about the state and what can be done/displayed
    get state() {
      // We need to clone the entry so that we don't impact the existing states object
      const entry = _.cloneDeep(_.find(states, ['key', self.status]) || _.find(states, ['key', 'UNKNOWN']));

      // The canConnect value is also determined by looking at the existing state requirement and
      // if we have any connections
      entry.canConnect = entry.canConnect && self.hasConnections;

      return entry;
    },

    getConnections(filterFn = () => true) {
      return _.filter(self.connections, filterFn);
    },

    // The region the workspace actually lives in, for display.
    //
    // `targetRegion` is only stored on records created after multi-region support landed; the create
    // path now defaults it to the primary region, so a record that has no value is one that predates
    // the field and can only ever have been launched in the primary region. Treating empty as the
    // primary region is the same reading the backend takes (`env.targetRegion || primaryRegion` in
    // environment-sc-service), and it keeps older workspaces from rendering with no region at all.
    get displayRegion() {
      return self.targetRegion || process.env.REACT_APP_AWS_REGION || '';
    },

    // The three scheduling facts a card can show, each sourced entirely from the Tag_Mirror so that
    // they cost nothing at render time (Requirement 11.5).
    //
    // Separate rather than one joined summary: the card shows them in different places now, because
    // they are not equally urgent. An outstanding idle warning is time-critical and belongs with the
    // alerts; a timetable is background information and belongs with the other facts about the
    // workspace. A single string could only ever land in one of the two.
    //
    // The wording lives in `schedule-display.js` and the grammar in `schedule-tag-format.js`
    // (Requirement 16.3); the model only says which tag feeds which phrase.

    // The timetable as a phrase, e.g. `day, weekend` or `weekdays 09:00 to 17:00`. The detail only:
    // it does not name itself as a schedule, because it is rendered as the tooltip of a marker that
    // already carries that word. Null when nothing is scheduled, which is the signal to render no
    // marker at all.
    get schedulePhrase() {
      return describeMirroredSchedule(_.isNil(self.schedule) ? '' : String(self.schedule));
    },

    // Null when no idle timeout is set.
    get idleStopPhrase() {
      return describeMirroredIdleStop(_.isNil(self.idleStopMinutes) ? null : Number(self.idleStopMinutes));
    },

    // The idle stop the scheduler already performed, as a phrase, or null when it recorded none or
    // recorded something unreadable. The timestamp itself stays on `idleStopStoppedAt` for the caller
    // to render relatively; this is only the wording.
    get idleStopEventPhrase() {
      return describeIdleStopEvent(_.isNil(self.idleStopStoppedAt) ? null : String(self.idleStopStoppedAt));
    },

    // True when the external scheduler has judged this workspace idle and has not yet stopped it.
    //
    // Decided from the timestamp alone. The tag is advisory and may be absent or malformed
    // (Requirement 4.10), so an unreadable value counts as no warning; but a warning with no readable
    // timeout to project from is still a warning, and is reported as one so the card can say the
    // workspace will stop soon rather than say nothing.
    get hasIdleStopWarning() {
      const warnedAt = _.isNil(self.idleStopWarnedAt) ? '' : String(self.idleStopWarnedAt).trim();
      if (warnedAt.length === 0) return false;

      return !Number.isNaN(new Date(warnedAt).getTime());
    },
  }));

export { ScEnvironment };
